/**
 * Audit UI Selectors
 *
 * Memoized selectors for accessing audit UI state.
 * Only selects pure UI state - derived values from RTK Query
 * should be computed via hooks, not selectors.
 */

import { createSelector } from '@reduxjs/toolkit';
import type { FeatureSelectorWithFallback } from '@store/storeTypes';

import type { AuditUiState } from './auditUiSlice';
import type { DismissalsState } from './dismissalsSlice';

// ============================================================================
// Empty State Fallback
// ============================================================================

const emptyState: AuditUiState = {
  highlightedSectionId: null,
  sidebarMode: 'chat',
  dismissedPayerBannerNoteIds: [],
  dismissedAddTemplateBannerNoteIds: [],
  cardStates: {},
  updatingRuleId: null,
  preFixSectionIds: [],
  preFixSnapshotKeys: [],
};

// ============================================================================
// Base Selector with Fallback
// ============================================================================

/**
 * Selects the audit UI feature state with fallback
 * Uses any type for state to handle dynamic feature slice registration
 */
export const selectAuditUiState: FeatureSelectorWithFallback<AuditUiState, typeof emptyState> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
) => state['jotaudit/ui'] || emptyState;

// ============================================================================
// UI State Selectors
// ============================================================================

/**
 * Selects the highlighted section ID
 */
export const selectHighlightedSectionId = createSelector(
  [selectAuditUiState],
  state => state.highlightedSectionId,
);

/**
 * Selects the sidebar mode
 */
export const selectAuditSidebarMode = createSelector(
  [selectAuditUiState],
  state => state.sidebarMode,
);

/**
 * Selects the array of note IDs where the payer-aware banner has been dismissed
 */
export const selectDismissedPayerBannerNoteIds = createSelector(
  [selectAuditUiState],
  state => state.dismissedPayerBannerNoteIds,
);

/**
 * Factory selector to check if payer banner is dismissed for a specific note
 */
export const makeSelectIsPayerBannerDismissedForNote = (noteId: string) =>
  createSelector([selectDismissedPayerBannerNoteIds], dismissedNoteIds =>
    dismissedNoteIds.includes(noteId),
  );

/**
 * Selects the array of note IDs where the "add template" banner has been dismissed
 */
export const selectDismissedAddTemplateBannerNoteIds = createSelector(
  [selectAuditUiState],
  state => state.dismissedAddTemplateBannerNoteIds,
);

// ============================================================================
// Card State Selectors
// ============================================================================

/**
 * Selects the full card states record
 */
export const selectCardStates = createSelector(
  [selectAuditUiState],
  state => state.cardStates,
);

/**
 * Factory selector: card status for a specific rule (defaults to 'idle')
 */
export const makeSelectCardStatus = (ruleId: string) =>
  createSelector([selectCardStates], cardStates => cardStates[ruleId]?.status ?? 'idle');

/**
 * Factory selector: affected section IDs for a specific rule card
 */
export const makeSelectCardAffectedSections = (ruleId: string) =>
  createSelector([selectCardStates], cardStates => cardStates[ruleId]?.affectedSectionIds ?? []);

/**
 * Factory selector: how a card was completed ('ai' | 'manual' | undefined)
 */
export const makeSelectCardCompletedVia = (ruleId: string) =>
  createSelector([selectCardStates], cardStates => cardStates[ruleId]?.completedVia);

/**
 * Factory selector: whether a card has been graduated
 */
export const makeSelectCardGraduated = (ruleId: string) =>
  createSelector([selectCardStates], cardStates => !!cardStates[ruleId]?.graduated);

/**
 * Factory selector: AI change summary for a completed card
 */
export const makeSelectCardChangeSummary = (ruleId: string) =>
  createSelector([selectCardStates], cardStates => cardStates[ruleId]?.changeSummary);

/**
 * Selects the pre-fix section IDs (snapshot before the current AI fix started)
 */
export const selectPreFixSectionIds = createSelector(
  [selectAuditUiState],
  state => state.preFixSectionIds,
);

/**
 * Selects the currently updating rule ID (if any)
 */
export const selectUpdatingRuleId = createSelector(
  [selectAuditUiState],
  state => state.updatingRuleId,
);

/**
 * Whether any card is currently in 'updating' or 'interrupted' state.
 * Used to disable other cards' approve buttons while a fix is in progress.
 */
export const selectAnyCardUpdating = createSelector(
  [selectAuditUiState],
  state => state.updatingRuleId !== null,
);

// ============================================================================
// Dismissals State Selectors
// ============================================================================

/**
 * Empty dismissals state fallback
 */
const emptyDismissalsState: DismissalsState = {
  dismissedCardIds: [],
};

/**
 * Selects the dismissals feature state with fallback
 * Uses any type for state to handle dynamic feature slice registration
 */
export const selectDismissalsState: FeatureSelectorWithFallback<
  DismissalsState,
  typeof emptyDismissalsState
> = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
) => state['jotaudit/dismissals'] || emptyDismissalsState;

/**
 * Selects the array of dismissed card IDs
 */
export const selectDismissedCardIds = createSelector(
  [selectDismissalsState],
  dismissals => dismissals.dismissedCardIds,
);

/**
 * Factory selector to check if a specific card is dismissed
 */
export const makeSelectIsCardDismissed = (cardId: string) =>
  createSelector([selectDismissedCardIds], dismissedIds =>
    dismissedIds.includes(cardId),
  );
